import {
  Link,
  LinkButton,
  SparkIcon,
  SparkLendIcon,
  SparkLiquidityLayerIcon,
  SparkSavingsIcon,
  SPKIcon
} from '@/components';
import {
  ArrowUpRightBoxIcon,
  Box,
  BoxProps,
  Card,
  CollapsableSidebar,
  CrossIcon,
  Flex,
  FlexProps,
  LinkWrapper,
  ListIcon,
  useIsMobile,
  useToggle
} from '@blockanalitica/ui';
import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router';
import styled, { css } from 'styled-components';

export interface HeaderLinkProps {
  $hoverBorderColor: string;
}

const HeaderLink = styled(LinkWrapper)<HeaderLinkProps>`
  display: flex;
  align-items: center;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  ${(props) => css`
    color: ${props.theme.colors.text};
    gap: ${props.theme.space['2xs']};
    padding: ${props.theme.space['2xs']} ${props.theme.space['s']};
    border-radius: ${props.theme.space['2xs']};
    border: solid 1px ${props.theme.colors.fgSecondary};
    background-color: ${props.theme.colors.fgPrimary};
    font-size: ${props.theme.sizes['1']};

    &:hover,
    &.active {
      border-color: ${props.theme.colors[props.$hoverBorderColor]};
      color: ${props.theme.colors.text};
    }
  `}
`;

const MobileHeaderLink = styled(LinkWrapper)<HeaderLinkProps>`
  display: flex;
  align-self: stretch;
  align-items: center;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  ${(props) => css`
    color: ${props.theme.colors.text};
    gap: ${props.theme.space['2xs']};
    font-size: ${props.theme.sizes['2']};

    &:hover,
    &.active {
    }
  `}
`;

const LogoWrapper = styled(Card)`
  & svg {
    filter: brightness(500%);
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover svg {
    filter: brightness(100%);
  }
`;

const CloseButton = styled(Box)<BoxProps>`
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const MenuButtonWrapper = styled(Flex)<FlexProps>`
  border-radius: ${(props) => props.theme.space['s']};
`;

const MenuButton = styled(Flex)<FlexProps>`
  cursor: pointer;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
  ${(props) => css`
    border-radius: ${props.theme.space['xs']};
    border: solid 1px ${props.theme.colors.fgSecondary};
    background-color: ${props.theme.colors.fgPrimary};
  `}
`;

const AlphaBackgroundLayer = styled.div`
  display: block;
  position: fixed;
  inset: 0;
  background-color: ${(props) => props.theme.colors.black65};
  z-index: 49;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

export default function Header() {
  const [sidebarOpen, toggleSidebar] = useToggle();
  const isMobile = useIsMobile();

  const location = useLocation();

  useEffect(() => {
    toggleSidebar(false);
  }, [location.pathname, toggleSidebar]);

  return (
    <>
      <Flex gap="xs" justifyContent={['space-between', 'flex-start']}>
        <LinkWrapper
          // @ts-ignore
          as={Link}
          to="/"
          display="flex">
          <LogoWrapper
            flex="0"
            padding={['s m', 'xs s']}
            alignItems="center"
            justifyContent="center"
            borderRadius={['1', '-1']}>
            <SparkIcon size="5" />
          </LogoWrapper>
        </LinkWrapper>

        <Card
          flexDirection="row"
          alignItems="center"
          gap="xs"
          padding="xs"
          justifyContent="space-between"
          backgroundColor="background"
          borderRadius={['1', '-1']}
          flex={isMobile ? '0' : '1'}>
          {isMobile ? (
            <>
              <CollapsableSidebar
                sidebarOpen={sidebarOpen}
                toggleSidebar={toggleSidebar}
                alignItems="flex-start"
                gap="xl">
                <Flex
                  justifyContent="space-between"
                  width="100%"
                  paddingTop="s">
                  <SparkIcon size="5" />
                  <CloseButton
                    alignSelf="center"
                    onClick={() => toggleSidebar(false)}>
                    <CrossIcon size="3" />
                  </CloseButton>
                </Flex>
                <Flex flexDirection="column" width="100%" gap="m">
                  <MobileHeaderLink
                    as={NavLink}
                    to="/spark-liquidity-layer"
                    $hoverBorderColor="sparkLiquidityLayerColor">
                    <SparkLiquidityLayerIcon size="1" /> Spark Liquidity Layer
                  </MobileHeaderLink>
                  <MobileHeaderLink
                    as={NavLink}
                    to="/savings"
                    $hoverBorderColor="sparkSavingsColor">
                    <SparkSavingsIcon size="1" /> Savings
                  </MobileHeaderLink>
                  <MobileHeaderLink
                    as={NavLink}
                    to="/sparklend"
                    $hoverBorderColor="sparkLendColor">
                    <SparkLendIcon size="1" /> SparkLend
                  </MobileHeaderLink>
                  <MobileHeaderLink
                    as={NavLink}
                    to="/spk"
                    $hoverBorderColor="sparkSpkColor">
                    <SPKIcon size="1" />
                    SPK Token
                  </MobileHeaderLink>
                </Flex>
              </CollapsableSidebar>
              <MenuButtonWrapper>
                <MenuButton onClick={() => toggleSidebar()} padding="3xs 2xs">
                  <ListIcon size="4" />
                </MenuButton>
              </MenuButtonWrapper>
            </>
          ) : (
            <>
              <Flex gap="xs">
                <HeaderLink
                  as={NavLink}
                  to="/spark-liquidity-layer"
                  $hoverBorderColor="sparkLiquidityLayerColor">
                  <SparkLiquidityLayerIcon size="2" /> Spark Liquidity Layer
                </HeaderLink>
                <HeaderLink
                  as={NavLink}
                  to="/savings"
                  $hoverBorderColor="sparkSavingsColor">
                  <SparkSavingsIcon size="2" /> Savings
                </HeaderLink>

                <HeaderLink
                  as={NavLink}
                  to="/sparklend"
                  $hoverBorderColor="sparkLendColor">
                  <SparkLendIcon size="2" /> SparkLend
                </HeaderLink>
                <HeaderLink
                  as={NavLink}
                  to="/spk"
                  $hoverBorderColor="sparkSpkColor">
                  <SPKIcon size="2" />
                  SPK Token
                </HeaderLink>
              </Flex>
              <LinkButton
                as="a"
                // @ts-ignore
                href="https://app.spark.fi/"
                target="_blank"
                rel="noopener noreferrer"
                variant="fancy"
                alignItems="center"
                gap="2xs">
                Launch App <ArrowUpRightBoxIcon size="0" />
              </LinkButton>
            </>
          )}
        </Card>
      </Flex>
      {sidebarOpen ? (
        <AlphaBackgroundLayer onClick={() => toggleSidebar()} />
      ) : null}
    </>
  );
}
